import { Drilldown } from '@data/components/report/ReportFilterBar/ReportFilterBar';
import { useGetClassesByEdAppApi } from '@data/hooks/api/use-get-classes-by-ed-app-api/use-get-classes-by-ed-app-api';
import { useGetStudentsByClassAndEdAppApi } from '@data/hooks/api/use-get-students-by-class-and-ed-app-api/use-get-students-by-class-and-ed-app-api';

/**
 * The classes the figures cover: the selection, or every class the dropdown
 * offers when none is picked.
 *
 * Empty until the list answers, and the reports hold their request until it
 * does — the service is told which classes to read rather than left to infer
 * "all of them", the same way the admin reports name every organization and
 * every educator.
 */
export function useEffectiveClassIds({
  orgId,
  userId,
  edApp,
  selectedClassIds,
  productCodes,
  enabled = true,
}: {
  orgId: string;
  userId: string;
  edApp: string;
  selectedClassIds: string[];
  /** Overrides the signed-in user's own entitlements; see the classes hook. */
  productCodes?: string[] | undefined;
  enabled?: boolean;
}): string[] {
  const { data: classes } = useGetClassesByEdAppApi(
    { orgId, userId, edApp, ...(productCodes ? { productCodes } : {}) },
    { enabled, retry: 0 }
  );

  if (selectedClassIds.length > 0) return selectedClassIds;

  return (classes ?? []).map((cls) => cls.classId);
}

/**
 * The class whose students the reader can filter by.
 *
 * Only the levels that show a student dropdown have one. The all-classes level
 * does not, so picking a single class there still means that whole class —
 * naming its students would narrow the request to a filter nobody set.
 */
/**
 * The levels that show a student dropdown, named rather than excluded: a level
 * added to the enum later has no student filter until it says it does, which
 * is the safer default — the levels above these report on classes.
 */
const LEVELS_WITH_A_STUDENT_FILTER: Drilldown[] = [
  Drilldown.Class,
  Drilldown.Student,
  Drilldown.EngagementFeed,
];

function studentFilterClassId(
  drilldown: Drilldown,
  classIds: string[]
): string {
  if (!LEVELS_WITH_A_STUDENT_FILTER.includes(drilldown)) return '';
  return classIds.length === 1 ? classIds[0] ?? '' : '';
}

/** The individual class view reports on its students, not on the class itself. */
function reportsOnStudents(drilldown: Drilldown): boolean {
  return drilldown === Drilldown.Class || drilldown === Drilldown.Student;
}

/**
 * The students the figures cover, read the same way as the classes above.
 *
 * `isReady` is what the caller gates its request on. Where the level has no
 * student dropdown it is true straight away and `studentIds` stays null —
 * there is no roster to wait for. Where the view is about the students
 * themselves it stays false unless the class has some entitled to the product:
 * there is nothing to ask the service for.
 */
export function useEffectiveStudentIds({
  drilldown,
  classIds,
  edApp,
  selectedStudentIds,
  productCodes,
  enabled = true,
}: {
  drilldown: Drilldown;
  classIds: string[];
  edApp: string;
  selectedStudentIds: string[];
  /** Overrides the signed-in user's own entitlements; see the students hook. */
  productCodes?: string[] | undefined;
  enabled?: boolean;
}): { studentIds: string[] | null; isReady: boolean } {
  const classId = studentFilterClassId(drilldown, classIds);
  const { data: students, isPending } = useGetStudentsByClassAndEdAppApi(
    { classId, edApp, ...(productCodes ? { productCodes } : {}) },
    { enabled: enabled && !!classId, retry: 0 }
  );

  if (!classId) return { studentIds: null, isReady: true };
  if (selectedStudentIds.length > 0) {
    return { studentIds: selectedStudentIds, isReady: true };
  }

  const everyStudentId = (students ?? []).map((student) => student.id);
  if (everyStudentId.length > 0) {
    return { studentIds: everyStudentId, isReady: true };
  }

  return {
    studentIds: null,
    isReady: !isPending && !reportsOnStudents(drilldown),
  };
}
